import { useState } from 'react';
import {
  Star, Trophy, Flame, Target, TrendingUp, Shield, Crown, Gem,
  Users, Megaphone, Heart, Layers, Database, Footprints, Award, Lock,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '@hooks/useAuth';
import { useGamification } from '@hooks/useGamification';
import PageHeader from '@components/layout/PageHeader';
import Loading from '@components/common/Loading';
import PageTutorial from '@components/common/PageTutorial';
import { clsx } from 'clsx';

const ICON_MAP = {
  footprints: Footprints,
  flame: Flame,
  trophy: Trophy,
  crown: Crown,
  target: Target,
  shield: Shield,
  users: Users,
  megaphone: Megaphone,
  network: Users,
  'trending-up': TrendingUp,
  heart: Heart,
  layers: Layers,
  database: Database,
  star: Star,
  gem: Gem,
};

const LEVEL_COLORS = {
  1: 'from-gray-400 to-gray-500',
  2: 'from-green-400 to-green-600',
  3: 'from-blue-400 to-blue-600',
  4: 'from-indigo-400 to-indigo-600',
  5: 'from-purple-400 to-purple-600',
  6: 'from-pink-400 to-pink-600',
  7: 'from-orange-400 to-orange-600',
  8: 'from-red-400 to-red-600',
  9: 'from-yellow-400 to-amber-600',
  10: 'from-yellow-300 to-yellow-500',
};

const XPBar = ({ xp, currentLevelXP, nextLevelXP, progress }) => {
  return (
    <div className="w-full">
      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
        <span>{xp?.toLocaleString('pt-BR')} XP</span>
        <span>{nextLevelXP ? `${nextLevelXP.toLocaleString('pt-BR')} XP` : 'MAX'}</span>
      </div>
      <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full transition-all duration-700 ease-out"
          style={{ width: `${Math.min(100, progress || 0)}%` }}
        />
      </div>
    </div>
  );
};

const LevelBadge = ({ level, size = 'lg' }) => {
  const colorClass = LEVEL_COLORS[level] || LEVEL_COLORS[1];
  const sizeClasses = size === 'lg'
    ? 'w-20 h-20 text-3xl'
    : size === 'md' ? 'w-12 h-12 text-lg' : 'w-8 h-8 text-sm';

  return (
    <div className={clsx(
      'rounded-full bg-gradient-to-br flex items-center justify-center text-white font-bold shadow-lg',
      colorClass, sizeClasses
    )}>
      {level}
    </div>
  );
};

const AchievementCard = ({ achievement }) => {
  const IconComponent = ICON_MAP[achievement.icon] || Award;
  const unlocked = achievement.unlocked;

  return (
    <div className={clsx(
      'relative rounded-2xl border p-4 transition-all',
      unlocked
        ? 'bg-white dark:bg-gray-800 border-primary-200 dark:border-primary-800 shadow-sm'
        : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 opacity-60'
    )}>
      <div className="flex items-start gap-3">
        <div className={clsx(
          'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0',
          unlocked
            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
            : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
        )}>
          {unlocked ? <IconComponent size={20} /> : <Lock size={20} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className={clsx(
            'font-semibold text-sm',
            unlocked ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
          )}>
            {achievement.name}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {achievement.description}
          </p>
          <div className="flex items-center gap-2 mt-1.5">
            <span className={clsx(
              'text-xs font-bold px-2 py-0.5 rounded-full',
              unlocked
                ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
            )}>
              +{achievement.xp} XP
            </span>
            {unlocked && achievement.unlockedAt && (
              <span className="text-[10px] text-gray-400">
                {new Date(achievement.unlockedAt).toLocaleDateString('pt-BR')}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Gamification = () => {
  const { user } = useAuth();
  const { profile, leaderboard, definitions, isLoadingProfile } = useGamification();
  const [activeTab, setActiveTab] = useState('overview');

  if (isLoadingProfile) return <Loading text="Carregando seu perfil..." />;

  const achievements = profile?.achievements || [];
  const unlockedCount = achievements.filter(a => a.unlocked).length;
  const totalCount = achievements.length;

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title="Meu Nivel"
        subtitle="Acompanhe sua evolucao financeira"
      />

      <PageTutorial
        pageId="gamification"
        title="Sistema de Niveis"
        steps={[
          'Ganhe XP registrando transacoes, completando metas e mantendo seu streak diario',
          'Desbloqueie conquistas especiais conforme avanca nos niveis',
          'Suba de nivel e compare com outros membros do seu plano familia',
        ]}
      />

      {/* Profile Hero */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className={clsx(
          'bg-gradient-to-r p-6',
          LEVEL_COLORS[profile?.level || 1]
        )}>
          <div className="flex items-center gap-5">
            <LevelBadge level={profile?.level || 1} />
            <div className="flex-1 text-white">
              <p className="text-2xl font-bold">{profile?.levelTitle || 'Iniciante'}</p>
              <p className="text-sm text-white/80 mt-0.5">Nivel {profile?.level || 1}</p>
              <p className="text-sm text-white/70 mt-1">{user?.name}</p>
            </div>
            <div className="text-right text-white">
              <div className="flex items-center gap-1.5">
                <Flame size={18} className="text-orange-300" />
                <span className="text-2xl font-bold">{profile?.streak || 0}</span>
              </div>
              <p className="text-xs text-white/70">dias seguidos</p>
            </div>
          </div>
        </div>
        <div className="p-5">
          <XPBar
            xp={profile?.xp || 0}
            currentLevelXP={profile?.currentLevelXP}
            nextLevelXP={profile?.nextLevelXP}
            progress={profile?.xpProgress || 0}
          />
          {profile?.nextLevelTitle && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 flex items-center gap-1">
              Proximo nivel: <strong>{profile.nextLevelTitle}</strong>
              <ChevronRight size={12} />
              faltam {((profile?.nextLevelXP || 0) - (profile?.xp || 0)).toLocaleString('pt-BR')} XP
            </p>
          )}
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'XP Total', value: (profile?.xp || 0).toLocaleString('pt-BR'), icon: Star, color: 'text-yellow-500' },
          { label: 'Streak Atual', value: `${profile?.streak || 0} dias`, icon: Flame, color: 'text-orange-500' },
          { label: 'Maior Streak', value: `${profile?.longestStreak || 0} dias`, icon: Trophy, color: 'text-purple-500' },
          { label: 'Conquistas', value: `${unlockedCount}/${totalCount}`, icon: Award, color: 'text-primary-500' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <stat.icon size={18} className={stat.color} />
            <p className="text-lg font-bold text-gray-900 dark:text-white mt-2">{stat.value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex items-center bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-1 w-fit">
        {[
          { id: 'overview', label: 'Conquistas' },
          { id: 'levels', label: 'Niveis' },
          { id: 'leaderboard', label: 'Ranking' },
          { id: 'xp-guide', label: 'Como ganhar XP' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              activeTab === tab.id
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Conquistas Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {achievements
            .sort((a, b) => (b.unlocked ? 1 : 0) - (a.unlocked ? 1 : 0))
            .map((a) => (
              <AchievementCard key={a.id} achievement={a} />
            ))}
        </div>
      )}

      {/* Niveis Tab */}
      {activeTab === 'levels' && (
        <div className="space-y-2">
          {(definitions?.levels || []).map((lvl) => {
            const isCurrentOrPast = (profile?.level || 1) >= lvl.level;
            const isCurrent = (profile?.level || 1) === lvl.level;
            return (
              <div
                key={lvl.level}
                className={clsx(
                  'flex items-center gap-4 p-4 rounded-xl border transition-all',
                  isCurrent
                    ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-300 dark:border-primary-700 ring-2 ring-primary-200 dark:ring-primary-800'
                    : isCurrentOrPast
                      ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                      : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 opacity-50'
                )}
              >
                <LevelBadge level={lvl.level} size="sm" />
                <div className="flex-1">
                  <p className={clsx(
                    'font-semibold',
                    isCurrentOrPast ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
                  )}>
                    {lvl.title}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {lvl.xp.toLocaleString('pt-BR')} XP necessarios
                  </p>
                </div>
                {isCurrent && (
                  <span className="text-xs font-bold text-primary-600 dark:text-primary-400 bg-primary-100 dark:bg-primary-900/30 px-3 py-1 rounded-full">
                    ATUAL
                  </span>
                )}
                {isCurrentOrPast && !isCurrent && (
                  <span className="text-xs text-green-600 dark:text-green-400">Concluido</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Leaderboard Tab */}
      {activeTab === 'leaderboard' && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {leaderboard.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <Trophy size={32} className="mx-auto mb-2 text-gray-300" />
              <p>Ainda nao ha ranking disponivel</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {leaderboard.map((entry, idx) => (
                <div key={entry.userId} className={clsx(
                  'flex items-center gap-4 px-5 py-3',
                  entry.userId === user?.userId && 'bg-primary-50 dark:bg-primary-900/10'
                )}>
                  <span className={clsx(
                    'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold',
                    idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                    idx === 1 ? 'bg-gray-200 text-gray-600' :
                    idx === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                  )}>
                    {idx + 1}
                  </span>
                  <LevelBadge level={entry.level || 1} size="sm" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white text-sm">
                      {entry.userId === user?.userId ? `${user.name} (voce)` : `Usuario ${idx + 1}`}
                    </p>
                    <p className="text-xs text-gray-500">{entry.levelTitle}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-sm text-gray-900 dark:text-white">{(entry.xp || 0).toLocaleString('pt-BR')} XP</p>
                    <p className="text-xs text-gray-500">Nivel {entry.level || 1}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* XP Guide Tab */}
      {activeTab === 'xp-guide' && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {[
              { action: 'Registrar transacao', xp: 5, freq: 'Por transacao' },
              { action: 'Categorizar transacao importada', xp: 3, freq: 'Por transacao' },
              { action: 'Completar meta de economia', xp: 50, freq: 'Por meta' },
              { action: 'Contribuir para uma meta', xp: 10, freq: 'Por contribuicao' },
              { action: 'Streak diario (registrar algo no dia)', xp: 15, freq: 'Por dia' },
              { action: 'Streak semanal completo (7 dias)', xp: 50, freq: 'Bonus semanal' },
              { action: 'Convidar alguem que se cadastrou', xp: 100, freq: 'Por convite' },
              { action: 'Primeiro login do mes', xp: 10, freq: 'Mensal' },
              { action: 'Configurar orcamento mensal', xp: 20, freq: 'Mensal' },
              { action: 'Atingir meta de orcamento do mes', xp: 80, freq: 'Mensal' },
            ].map((item) => (
              <div key={item.action} className="flex items-center justify-between px-5 py-3.5">
                <div>
                  <p className="font-medium text-sm text-gray-900 dark:text-white">{item.action}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{item.freq}</p>
                </div>
                <span className="text-sm font-bold text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 px-3 py-1 rounded-full">
                  +{item.xp} XP
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Gamification;
